/**
 * OrganizationsComparisonSection - Stacked bar chart for organization performance comparison
 * Shows organization achievement (green) vs remaining (gray) using recharts
 */

import React, { useState, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  Platform,
  Modal,
  Pressable,
  ScrollView,
  useWindowDimensions,
} from 'react-native';
import {
  BarChart,
  Bar,
  Cell,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { OrganizationComparison } from '../types';
import { useTheme } from '../contexts/ThemeContext';
import { useLanguage } from '../contexts/LanguageContext';
import { useAutoTranslate } from '../hooks/useAutoTranslate';
import { FONTS } from '../constants/config';
import { commitmentColor, METRIC_GREEN, METRIC_ORANGE, METRIC_RED } from '../utils/metricColors';

// Props
interface OrganizationsComparisonSectionProps {
  data: OrganizationComparison[];
  loading?: boolean;
  sortBy: string;
  order: 'asc' | 'desc';
  onSortChange: (field: string, order: 'asc' | 'desc') => void;
  // Default date range derived from strategic goals (earliest start_date,
  // latest target_completion_date). When the user picks a date in the inputs
  // it overrides these defaults but stays within their bounds.
  goalsFromDate?: string | null;
  goalsToDate?: string | null;
  // Strategic goals — used to filter the chart by date range. Each goal has
  // department_id (= org id) and start/end dates; orgs with at least one goal
  // overlapping [fromDate, toDate] stay visible in the chart.
  goals?: Array<{ department_id: number; start_date?: string; target_completion_date: string }>;
}

// Bar color uses the unified commitment scheme:
//   >= 80 → green (excellent), 50–79 → orange (moderate), < 50 → red (poor).
const getBarColor = (percentage: number): string => commitmentColor(percentage);

// Get label key for status based on percentage (matches getBarColor buckets).
const getStatusLabelKey = (percentage: number): string => {
  if (percentage >= 80) return 'charts.excellent';
  if (percentage >= 50) return 'charts.good';
  return 'charts.needsImprovement';
};

// Custom XAxis tick that wraps long Arabic department names onto
// multiple SVG lines. Recharts renders the default tick as a single
// <text> with white-space:nowrap, which clips long names with an
// ellipsis. We measure word-by-word and emit one <tspan> per line so
// the full name is always visible.
const WrappedAxisTick = (props: any) => {
  const { x, y, payload, fontSize = 11, fill, fontFamily, maxCharsPerLine = 14, maxLines = 4, lineHeight = 13 } = props;
  const value: string = (payload && payload.value) || '';

  const words = value.split(' ').filter(Boolean);
  const lines: string[] = [];
  let current = '';
  for (const word of words) {
    const candidate = current ? `${current} ${word}` : word;
    if (candidate.length <= maxCharsPerLine) {
      current = candidate;
    } else {
      if (current) lines.push(current);
      // very long single word → hard-split
      if (word.length > maxCharsPerLine) {
        for (let i = 0; i < word.length; i += maxCharsPerLine) {
          lines.push(word.slice(i, i + maxCharsPerLine));
        }
        current = '';
      } else {
        current = word;
      }
    }
  }
  if (current) lines.push(current);
  const visible = lines.slice(0, maxLines);

  return (
    <g transform={`translate(${x},${y})`}>
      <text
        textAnchor="middle"
        fill={fill}
        fontSize={fontSize}
        fontFamily={fontFamily}
      >
        {visible.map((line, i) => (
          <tspan key={i} x={0} dy={i === 0 ? fontSize : lineHeight}>
            {line}
          </tspan>
        ))}
      </text>
    </g>
  );
};

// Tooltip Content Component for Modal
const TooltipContent: React.FC<{
  org: OrganizationComparison;
  translateOrg: (name: string | undefined | null) => string;
  t: (key: string) => string;
}> = ({ org, translateOrg, t }) => (
  <View style={tooltipStyles.content}>
    <Text style={tooltipStyles.title}>{translateOrg(org.name)}</Text>
    <View style={tooltipStyles.divider} />

    {/* Performance Metrics */}
    <View style={tooltipStyles.metricRow}>
      <Text style={tooltipStyles.metricValue}>%{org.vision_achievement}</Text>
      <Text style={tooltipStyles.metricLabel}>{t('organizations.visionAchievementIndex')}</Text>
    </View>
    <View style={tooltipStyles.metricRow}>
      <Text style={tooltipStyles.metricValue}>%{org.commitment_index}</Text>
      <Text style={tooltipStyles.metricLabel}>{t('organizations.commitmentIndexLabel')}</Text>
    </View>
    <View style={tooltipStyles.metricRow}>
      <Text style={tooltipStyles.metricValue}>{org.performance_deviation != null ? `%${org.performance_deviation}` : '--'}</Text>
      <Text style={tooltipStyles.metricLabel}>{t('organizations.performanceDeviationIndex')}</Text>
    </View>

    <View style={tooltipStyles.divider} />

    {/* Counts */}
    <View style={tooltipStyles.metricRow}>
      <Text style={tooltipStyles.metricValue}>{org.goals_achieved}/{org.total_goals}</Text>
      <Text style={tooltipStyles.metricLabel}>{t('organizations.goalsAchieved')}</Text>
    </View>
    <View style={tooltipStyles.metricRow}>
      <Text style={tooltipStyles.metricValue}>{org.initiatives_completed}/{org.total_initiatives}</Text>
      <Text style={tooltipStyles.metricLabel}>{t('organizations.initiativesCompleted')}</Text>
    </View>
    <View style={tooltipStyles.metricRow}>
      <Text style={tooltipStyles.metricValue}>{org.total_kpis}</Text>
      <Text style={tooltipStyles.metricLabel}>{t('organizations.totalIndicators')}</Text>
    </View>

    {/* Status Badge */}
    <View style={[tooltipStyles.statusBadge, { backgroundColor: getBarColor(org.vision_achievement) + '20' }]}>
      <Text style={[tooltipStyles.statusText, { color: getBarColor(org.vision_achievement) }]}>
        {t(getStatusLabelKey(org.vision_achievement))}
      </Text>
    </View>
  </View>
);

// Custom Tooltip for recharts (shadcn style)
const CustomChartTooltip = ({ active, payload }: any) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <View style={chartTooltipStyles.container}>
        <Text style={chartTooltipStyles.title}>{data.fullName}</Text>
        {payload.map((entry: any, index: number) => (
          <View key={index} style={chartTooltipStyles.row}>
            <View style={[chartTooltipStyles.dot, { backgroundColor: entry.fill }]} />
            <Text style={chartTooltipStyles.label}>{entry.dataKey}</Text>
            <Text style={chartTooltipStyles.value}>%{entry.value}</Text>
          </View>
        ))}
      </View>
    );
  }
  return null;
};

// Custom Legend Component (RTL) — static status categories matching the
// unified commitment color scheme used by the bars.
const CustomLegend = ({ poorLabel, moderateLabel, excellentLabel }: { poorLabel: string; moderateLabel: string; excellentLabel: string }) => (
  <View style={styles.legend}>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: METRIC_RED }]} />
      <Text style={styles.legendText}>{poorLabel}</Text>
    </View>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: METRIC_ORANGE }]} />
      <Text style={styles.legendText}>{moderateLabel}</Text>
    </View>
    <View style={styles.legendItem}>
      <View style={[styles.legendDot, { backgroundColor: METRIC_GREEN }]} />
      <Text style={styles.legendText}>{excellentLabel}</Text>
    </View>
  </View>
);

// Sort Option Button
const SortButton: React.FC<{
  label: string;
  field: string;
  currentSortBy: string;
  currentOrder: 'asc' | 'desc';
  onPress: (field: string) => void;
}> = ({ label, field, currentSortBy, currentOrder, onPress }) => {
  const isActive = currentSortBy === field;

  return (
    <TouchableOpacity
      style={[styles.sortButton, isActive && styles.sortButtonActive]}
      onPress={() => onPress(field)}
    >
      <Text style={[styles.sortButtonText, isActive && styles.sortButtonTextActive]}>
        {label}
      </Text>
      {isActive && (
        <Text style={styles.sortArrow}>
          {currentOrder === 'desc' ? '↓' : '↑'}
        </Text>
      )}
    </TouchableOpacity>
  );
};

// Main Component
export const OrganizationsComparisonSection: React.FC<OrganizationsComparisonSectionProps> = ({
  data,
  loading,
  sortBy,
  order,
  onSortChange,
  goalsFromDate,
  goalsToDate,
  goals,
}) => {
  const { width } = useWindowDimensions();
  const { colors, isDark } = useTheme();
  const { language, t } = useLanguage();
  const { translate, translateOrg } = useAutoTranslate();
  const isMobile = width < 768;

  // Date filter state — ISO yyyy-mm-dd. Defaults seed from strategic-goals
  // start/end dates passed from the parent. Fallback to calendar-year window
  // when goals haven't loaded yet.
  const today = new Date();
  const yearStart = new Date(today.getFullYear(), 0, 1);
  const toIso = (d: Date) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
  const [fromDate, setFromDate] = useState<string>(goalsFromDate ?? toIso(yearStart));
  const [toDate, setToDate] = useState<string>(goalsToDate ?? toIso(today));
  // Track whether user has manually changed dates so we don't overwrite their
  // selection when goals reload.
  const [userTouchedDates, setUserTouchedDates] = useState(false);
  React.useEffect(() => {
    if (userTouchedDates) return;
    if (goalsFromDate) setFromDate(goalsFromDate);
    if (goalsToDate) setToDate(goalsToDate);
  }, [goalsFromDate, goalsToDate, userTouchedDates]);

  // Lock the chart to commitment indicator only
  const ACTIVE_FIELD = 'commitment_index';

  // Org filter — null means "all"
  const [selectedOrgId, setSelectedOrgId] = useState<number | null>(null);
  const [showOrgPicker, setShowOrgPicker] = useState(false);

  const [tooltipOrg, setTooltipOrg] = useState<OrganizationComparison | null>(null);
  const [showTooltipModal, setShowTooltipModal] = useState(false);

  // Theme colors
  const themeColors = {
    sectionTitle: colors.textPrimary,
    sortButtonBg: isDark ? colors.backgroundAlt : '#F5F5F5',
    sortButtonText: colors.textSecondary,
    chartCard: colors.card,
    legendText: colors.textMuted,
    loadingText: colors.textMuted,
    emptyText: colors.textMuted,
    tooltipBg: colors.card,
    tooltipBorder: colors.cardBorder,
    tooltipTitle: colors.textPrimary,
    tooltipLabel: colors.textMuted,
    tooltipValue: colors.textPrimary,
    dividerColor: colors.border,
    gridColor: isDark ? '#4B5563' : '#E5E5E5',
    axisText: colors.textMuted,
    remainingBarColor: isDark ? '#4B5563' : '#E5E7EB',
  };

  // Handle sort button press
  const handleSortPress = useCallback((field: string) => {
    if (sortBy === field) {
      // Toggle order if same field
      onSortChange(field, order === 'desc' ? 'asc' : 'desc');
    } else {
      // Default to desc for new field
      onSortChange(field, 'desc');
    }
  }, [sortBy, order, onSortChange]);

  // Close tooltip
  const closeTooltip = useCallback(() => {
    setShowTooltipModal(false);
    setTooltipOrg(null);
  }, []);

  // Translated labels for chart
  const achievedLabel = t('charts.achieved');
  const poorLabel = t('charts.poor');
  const moderateLabel = t('charts.moderate');
  const excellentLabel = t('charts.excellent');

  // Build set of org IDs that have at least one goal whose [start, end] window
  // overlaps the picked [fromDate, toDate]. Goal overlap rule:
  //   start <= toDate  AND  end >= fromDate
  // When `goals` prop is missing, no date filtering is applied.
  const orgsWithinDateRange = React.useMemo(() => {
    if (!goals || goals.length === 0) return null;
    const ids = new Set<number>();
    for (const g of goals) {
      const start = g.start_date ?? g.target_completion_date;
      const end = g.target_completion_date;
      if (!start || !end) continue;
      if (start <= toDate && end >= fromDate) {
        ids.add(g.department_id);
      }
    }
    return ids;
  }, [goals, fromDate, toDate]);

  // Apply org dropdown filter; date-range filter only kicks in once the user
  // has picked a date manually — the default view (seeded from goals' min/max)
  // shows every org since the dashboard query is year-scoped and not all orgs
  // have goals in every year.
  const filteredData = data.filter((o) => {
    if (selectedOrgId != null && o.id !== selectedOrgId) return false;
    if (userTouchedDates && orgsWithinDateRange && !orgsWithinDateRange.has(o.id)) return false;
    return true;
  });

  // Drop orgs whose ACTIVE_FIELD is null/undefined; orgs with a real 0 value
  // are kept so they appear on the axis (even though their bar has no height)
  // — this keeps zero-progress orgs visible instead of silently hiding them.
  const chartData = [...filteredData]
    .filter((org) => {
      const value = org[ACTIVE_FIELD as keyof OrganizationComparison] as number | null | undefined;
      return value != null;
    })
    .sort((a, b) => {
      const aVal = a[ACTIVE_FIELD as keyof OrganizationComparison] as number || 0;
      const bVal = b[ACTIVE_FIELD as keyof OrganizationComparison] as number || 0;
      return bVal - aVal;
    })
    .map((org) => {
      const value = org[ACTIVE_FIELD as keyof OrganizationComparison] as number || 0;
      const translatedName = translateOrg(org.name);
      return {
        // Pass the full name to the chart; the custom XAxis tick wraps
        // long Arabic dept names onto multiple lines (see WrappedAxisTick
        // below). Truncating here was the original bug.
        name: translatedName,
        fullName: translateOrg(org.name),
        [achievedLabel]: Number(Number(value).toFixed(2)),
        rawValue: Number(value),
        originalData: org,
      };
    });

  const selectedOrgLabel = selectedOrgId == null
    ? t('organizations.allOrganizations')
    : translateOrg(data.find((o) => o.id === selectedOrgId)?.name);

  // Handle bar click for detailed modal
  const handleBarClick = (data: any) => {
    if (data && data.activePayload && data.activePayload[0]?.payload?.originalData) {
      setTooltipOrg(data.activePayload[0].payload.originalData);
      setShowTooltipModal(true);
    }
  };

  // Chart dimensions
  const chartHeight = 440;

  if (loading) {
    return (
      <View style={styles.container}>
        <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('organizations.performanceComparison')}</Text>
        <View style={[styles.loadingContainer, { backgroundColor: themeColors.chartCard }]}>
          <Text style={[styles.loadingText, { color: themeColors.loadingText }]}>{t('common.loading')}</Text>
        </View>
      </View>
    );
  }

  if (data.length === 0) {
    return (
      <View style={styles.container}>
        <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('organizations.performanceComparison')}</Text>
        <View style={[styles.emptyContainer, { backgroundColor: themeColors.chartCard }]}>
          <Text style={[styles.emptyText, { color: themeColors.emptyText }]}>{t('common.noData')}</Text>
        </View>
      </View>
    );
  }

  return (
    <View style={styles.container}>
      {/* Section Title */}
      <Text style={[styles.sectionTitle, { color: themeColors.sectionTitle }]}>{t('organizations.performanceComparison')}</Text>

      {/* Filters Row (Figma 6502:176389) */}
      <View style={styles.filtersRow}>
        {/* إلى تاريخ - To Date (left in RTL) */}
        <View style={styles.filterField}>
          <Text style={[styles.filterLabel, { color: themeColors.sectionTitle }]}>{t('organizations.toDate')}</Text>
          {Platform.OS === 'web' ? (
            React.createElement('input', {
              type: 'date',
              value: toDate,
              min: fromDate,
              onChange: (e: any) => { setToDate(e.target.value); setUserTouchedDates(true); },
              onClick: (e: any) => { try { e.currentTarget.showPicker?.(); } catch {} },
              onFocus: (e: any) => { try { e.currentTarget.showPicker?.(); } catch {} },
              style: {
                width: '100%',
                height: 32,
                paddingInline: 8,
                borderRadius: 8,
                border: `1px solid ${colors.cardBorder}`,
                backgroundColor: themeColors.chartCard,
                color: themeColors.sectionTitle,
                fontFamily: FONTS.regular,
                fontSize: 12,
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                outline: 'none',
                direction: 'rtl',
                cursor: 'pointer',
              },
            })
          ) : (
            <View style={[styles.filterInput, { backgroundColor: themeColors.chartCard, borderColor: colors.cardBorder }]}>
              <Text style={[styles.filterValue, { color: themeColors.sectionTitle }]}>{toDate}</Text>
            </View>
          )}
        </View>

        {/* من تاريخ - From Date (middle) */}
        <View style={styles.filterField}>
          <Text style={[styles.filterLabel, { color: themeColors.sectionTitle }]}>{t('organizations.fromDate')}</Text>
          {Platform.OS === 'web' ? (
            React.createElement('input', {
              type: 'date',
              value: fromDate,
              max: toDate,
              onChange: (e: any) => { setFromDate(e.target.value); setUserTouchedDates(true); },
              onClick: (e: any) => { try { e.currentTarget.showPicker?.(); } catch {} },
              onFocus: (e: any) => { try { e.currentTarget.showPicker?.(); } catch {} },
              style: {
                width: '100%',
                height: 32,
                paddingInline: 8,
                borderRadius: 8,
                border: `1px solid ${colors.cardBorder}`,
                backgroundColor: themeColors.chartCard,
                color: themeColors.sectionTitle,
                fontFamily: FONTS.regular,
                fontSize: 12,
                boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
                outline: 'none',
                direction: 'rtl',
                cursor: 'pointer',
              },
            })
          ) : (
            <View style={[styles.filterInput, { backgroundColor: themeColors.chartCard, borderColor: colors.cardBorder }]}>
              <Text style={[styles.filterValue, { color: themeColors.sectionTitle }]}>{fromDate}</Text>
            </View>
          )}
        </View>

        {/* الجهات - Organizations Dropdown (right in RTL) */}
        <View style={styles.filterField}>
          <Text style={[styles.filterLabel, { color: themeColors.sectionTitle }]}>{t('organizations.organizations')}</Text>
          <TouchableOpacity
            style={[styles.filterInput, { backgroundColor: themeColors.chartCard, borderColor: colors.cardBorder }]}
            onPress={() => setShowOrgPicker(true)}
            activeOpacity={0.7}
          >
            <Text style={[styles.filterIcon, { color: themeColors.axisText }]}>⌄</Text>
            <Text
              style={[styles.filterValue, { color: selectedOrgId == null ? themeColors.axisText : themeColors.sectionTitle }]}
              numberOfLines={1}
            >
              {selectedOrgLabel}
            </Text>
          </TouchableOpacity>
        </View>
      </View>

      {/* Organizations Picker Modal */}
      <Modal
        visible={showOrgPicker}
        transparent
        animationType="fade"
        onRequestClose={() => setShowOrgPicker(false)}
      >
        <Pressable style={tooltipStyles.backdrop} onPress={() => setShowOrgPicker(false)}>
          <Pressable
            style={[orgPickerStyles.container, { backgroundColor: themeColors.tooltipBg, borderColor: themeColors.tooltipBorder }]}
            onPress={(e) => e.stopPropagation()}
          >
            <ScrollView style={{ maxHeight: 320 }}>
              <TouchableOpacity
                style={orgPickerStyles.row}
                onPress={() => { setSelectedOrgId(null); setShowOrgPicker(false); }}
              >
                <Text style={[orgPickerStyles.rowText, selectedOrgId == null && orgPickerStyles.rowTextActive, { color: themeColors.sectionTitle }]}>
                  {t('organizations.allOrganizations')}
                </Text>
              </TouchableOpacity>
              {data.map((org) => (
                <TouchableOpacity
                  key={org.id}
                  style={orgPickerStyles.row}
                  onPress={() => { setSelectedOrgId(org.id); setShowOrgPicker(false); }}
                >
                  <Text
                    style={[
                      orgPickerStyles.rowText,
                      selectedOrgId === org.id && orgPickerStyles.rowTextActive,
                      { color: themeColors.sectionTitle }
                    ]}
                    numberOfLines={1}
                  >
                    {translateOrg(org.name)}
                  </Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </Pressable>
        </Pressable>
      </Modal>

      {/* Chart Card */}
      <View style={[styles.chartCard, { backgroundColor: themeColors.chartCard }]}>
        {Platform.OS === 'web' && data.length > 0 ? (
          <View style={{ height: chartHeight, width: '100%', minWidth: 300, minHeight: chartHeight }}>
            <ResponsiveContainer width="100%" height={chartHeight} debounce={50}>
              <BarChart
                data={chartData}
                margin={{ top: 20, right: 20, left: 20, bottom: 5 }}
                onClick={handleBarClick}
              >
                <CartesianGrid vertical={false} strokeDasharray="3 3" stroke={themeColors.gridColor} />
                <XAxis
                  dataKey="name"
                  tickLine={false}
                  tickMargin={10}
                  axisLine={false}
                  interval={0}
                  height={75}
                  tick={
                    <WrappedAxisTick
                      fontSize={11}
                      fill={themeColors.axisText}
                      fontFamily={FONTS.regular}
                    />
                  }
                />
                <YAxis
                  domain={[0, 100]}
                  ticks={[0, 10, 20, 30, 40, 50, 60, 70, 80, 90, 100]}
                  tickLine={false}
                  axisLine={false}
                  tick={{ fontSize: 11, fill: themeColors.axisText, fontFamily: FONTS.regular }}
                  tickFormatter={(value) => `${value}%`}
                />
                <Tooltip content={<CustomChartTooltip />} cursor={{ fill: isDark ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.05)' }} />
                <Legend content={<CustomLegend poorLabel={poorLabel} moderateLabel={moderateLabel} excellentLabel={excellentLabel} />} />
                <Bar
                  dataKey={achievedLabel}
                  radius={[2, 2, 0, 0]}
                  style={{ cursor: 'pointer' }}
                >
                  {chartData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={getBarColor(entry.rawValue)} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </View>
        ) : (
          <View style={[styles.emptyContainer, { backgroundColor: themeColors.chartCard }]}>
            <Text style={[styles.emptyText, { color: themeColors.emptyText }]}>{t('common.chartWebOnly')}</Text>
          </View>
        )}
      </View>

      {/* Tooltip Modal */}
      <Modal
        visible={showTooltipModal}
        transparent
        animationType="fade"
        onRequestClose={closeTooltip}
      >
        <Pressable style={tooltipStyles.backdrop} onPress={closeTooltip}>
          <View
            style={[
              tooltipStyles.container,
              tooltipStyles.containerMobile,
              { backgroundColor: themeColors.tooltipBg },
            ]}
          >
            {tooltipOrg && <TooltipContent org={tooltipOrg} translateOrg={translateOrg} t={t} />}
          </View>
        </Pressable>
      </Modal>
    </View>
  );
};

// Styles
const styles = StyleSheet.create({
  container: {
    marginTop: 24,
    marginBottom: 16,
  },
  sectionTitle: {
    fontSize: 18,
    color: '#171717',
    textAlign: 'right',
    marginBottom: 16,
    fontFamily: FONTS.medium,
  },
  sortScrollView: {
    marginBottom: 12,
  },
  sortOptions: {
    flexDirection: 'row-reverse',
    gap: 8,
    paddingHorizontal: 4,
  },
  filtersRow: {
    flexDirection: 'row',
    gap: 20,
    marginBottom: 16,
    width: '100%',
  },
  filterField: {
    flex: 1,
    gap: 6,
  },
  filterLabel: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right',
    lineHeight: 20,
  },
  filterInput: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    height: 32,
    paddingHorizontal: 6,
    borderWidth: 1,
    borderRadius: 8,
    boxShadow: '0px 1px 2px rgba(0, 0, 0, 0.05)',
  },
  filterIcon: {
    fontSize: 14,
  },
  filterValue: {
    flex: 1,
    fontSize: 12,
    fontFamily: FONTS.regular,
    textAlign: 'right',
  },
  sortButton: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 8,
    backgroundColor: '#F5F5F5',
    gap: 4,
  },
  sortButtonActive: {
    backgroundColor: '#A9211F',
  },
  sortButtonText: {
    fontSize: 13,
    color: '#525252',
    fontWeight: '500',
    fontFamily: FONTS.regular,
  },
  sortButtonTextActive: {
    color: '#FFFFFF',
  },
  sortArrow: {
    fontSize: 12,
    color: '#FFFFFF',
    fontWeight: '600',
    fontFamily: FONTS.medium,
  },
  chartCard: {
    borderRadius: 12,
    padding: 16,
    minWidth: 300,
    minHeight: 350,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 2px 8px rgba(0, 0, 0, 0.05)',
    elevation: 2,
  },
  legend: {
    flexDirection: 'row-reverse',
    justifyContent: 'center',
    gap: 24,
    marginTop: 16,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#E5E5E5',
  },
  legendItem: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
  },
  legendDot: {
    width: 12,
    height: 12,
    borderRadius: 2,
  },
  legendText: {
    fontSize: 12,
    color: '#737373',
    fontFamily: FONTS.regular,
  },
  loadingContainer: {
    borderRadius: 12,
    padding: 40,
    alignItems: 'center',
  },
  loadingText: {
    fontSize: 14,
    color: '#737373',
    fontFamily: FONTS.regular,
  },
  emptyContainer: {
    borderRadius: 12,
    padding: 40,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 14,
    color: '#737373',
    fontFamily: FONTS.regular,
  },
});

// Chart Tooltip Styles (shadcn style)
const chartTooltipStyles = StyleSheet.create({
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 8,
    padding: 12,
    borderWidth: 1,
    borderColor: '#E5E5E5',
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.1)',
    minWidth: 150,
  },
  title: {
    fontSize: 12,
    fontWeight: '500',
    color: '#525252',
    textAlign: 'right',
    marginBottom: 8,
    fontFamily: FONTS.regular,
  },
  row: {
    flexDirection: 'row-reverse',
    alignItems: 'center',
    gap: 8,
    marginBottom: 4,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: 2,
  },
  label: {
    fontSize: 12,
    color: '#525252',
    flex: 1,
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  value: {
    fontSize: 12,
    fontWeight: '600',
    color: '#171717',
    fontFamily: FONTS.medium,
  },
});

// Tooltip Styles
const orgPickerStyles = StyleSheet.create({
  container: {
    width: 320,
    maxWidth: '90%' as const,
    borderRadius: 8,
    borderWidth: 1,
    paddingVertical: 6,
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 8,
  },
  row: {
    paddingHorizontal: 16,
    paddingVertical: 10,
  },
  rowText: {
    fontSize: 14,
    fontFamily: FONTS.regular,
    textAlign: 'right' as const,
  },
  rowTextActive: {
    fontFamily: FONTS.medium,
  },
});

const tooltipStyles = StyleSheet.create({
  backdrop: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.3)',
    justifyContent: 'center',
    alignItems: 'center',
  },
  container: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    padding: 16,
    width: 260,
    // @ts-ignore - boxShadow is valid for web
    boxShadow: '0px 4px 12px rgba(0, 0, 0, 0.15)',
    elevation: 8,
  },
  containerMobile: {
    position: 'relative',
  },
  content: {
    gap: 8,
  },
  title: {
    fontSize: 15,
    fontWeight: '700',
    color: '#171717',
    textAlign: 'right',
    marginBottom: 4,
    fontFamily: FONTS.medium,
  },
  divider: {
    height: 1,
    backgroundColor: '#E5E5E5',
    marginVertical: 4,
  },
  metricRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  metricLabel: {
    fontSize: 12,
    color: '#737373',
    textAlign: 'right',
    fontFamily: FONTS.regular,
  },
  metricValue: {
    fontSize: 13,
    fontWeight: '600',
    color: '#171717',
    fontFamily: FONTS.medium,
  },
  statusBadge: {
    alignSelf: 'center',
    paddingHorizontal: 12,
    paddingVertical: 6,
    borderRadius: 16,
    marginTop: 8,
  },
  statusText: {
    fontSize: 12,
    fontWeight: '600',
    fontFamily: FONTS.medium,
  },
});

export default OrganizationsComparisonSection;
